import { PriceableItemOrigin } from "elephant-web/gql";
import type { SearchNewItemHookResult } from "./types";
import useSearchCustomItemLazyQuery from "./use-search-custom-item-lazy-query";
import useSearchGeneralItemResult from "./use-search-general-item-result";
import useSearchInvestigationItemLazyQuery from "./use-search-investigation-lazy-query";
import useSearchNewInventoryItemLazyQuery from "./use-search-new-inventory-lazy-query";
import useSearchProcedureItemLazyQuery from "./use-search-procedure-lazy-query";

/**
 * The one hook that will be called to power the AddNewItems table.
 * All other hooks will be called in here via a switch.
 *
 * It achieves conditional hook calling by being lazy. While this suits a pattern of using search fields, the small
 * drawback is the returned function will need to be called once on mount of the table.
 *
 * The interface is designed to mimic that of the graphql-codegen generated lazy queries, so that we might easily
 * pass arguments/use features from the generator should we need to, such as `fetchPolicy` or `defaultOptions`.
 */
export default function useSearchNewItemLazyQuery(
  origin: PriceableItemOrigin,
): SearchNewItemHookResult {
  const inventoryHookResult = useSearchNewInventoryItemLazyQuery();
  const investigationHookResult = useSearchInvestigationItemLazyQuery();
  const procedureHookResult = useSearchProcedureItemLazyQuery();
  const customHookResult = useSearchCustomItemLazyQuery();

  return {
    [PriceableItemOrigin.Custom]: customHookResult,
    [PriceableItemOrigin.General]: useSearchGeneralItemResult,
    [PriceableItemOrigin.Inventory]: inventoryHookResult,
    [PriceableItemOrigin.Investigation]: investigationHookResult,
    [PriceableItemOrigin.Procedure]: procedureHookResult,
  }[origin];
}
